import React, { useEffect, useState } from 'react';
import api from '../../utils/axios';
import { Plus, Edit3, Trash2, Check, X, RefreshCw } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';

const AdminCoupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    category: '',
    discount_percentage: 10,
    valid_from: new Date().toISOString().split('T')[0],
    valid_until: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    is_active: true
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    setLoading(true);
    try {
      const res = await api.get('/coupons');
      setCoupons(res.data.data);
    } catch (err) {
      setError('Failed to load coupons');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (coupon) => {
    setEditingCoupon(coupon);
    setFormData({ ...coupon });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this coupon?')) return;
    try {
      await api.delete(`/coupons/${id}`);
      fetchCoupons();
    } catch {
      setError('Failed to delete coupon');
    }
  };

  const handleActivate = async (id) => {
    try {
      const coupon = coupons.find(c => c.id === id);
      if (!coupon) return;
      await api.patch(`/coupons/${id}/activate`, {
        code: coupon.code,
        description: coupon.description,
        category: coupon.category,
        discount_percentage: coupon.discount_percentage,
        valid_from: coupon.valid_from,
        valid_until: coupon.valid_until,
        is_active: 1
      });
      fetchCoupons();
    } catch {
      setError('Failed to activate coupon');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (editingCoupon) {
        await api.put(`/coupons/${editingCoupon.id}`, formData);
      } else {
        await api.post('/coupons', formData);
      }
      setShowForm(false);
      setEditingCoupon(null);
      setFormData({ code: '', description: '', category: '', discount_percentage: 10, valid_from: new Date().toISOString().split('T')[0], valid_until: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], is_active: true });
      fetchCoupons();
    } catch {
      setError('Failed to save coupon');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">Manage Coupons</h1>
        <button
          className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg shadow hover:bg-primary-700 transition"
          onClick={() => { setShowForm(true); setEditingCoupon(null); setFormData({ code: '', description: '', category: '', discount_percentage: 10, valid_from: new Date().toISOString().split('T')[0], valid_until: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], is_active: true }); }}
        >
          <Plus className="w-4 h-4" /> Add Coupon
        </button>
      </div>
      {loading ? (
        <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin" /></div>
      ) : (
        <div className="grid gap-4">
          {coupons.map(coupon => (
            <div key={coupon.id} className={`flex flex-col sm:flex-row items-center justify-between bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-100 dark:border-gray-700 p-4 ${coupon.is_active ? '' : 'opacity-60'}`}>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <span className="font-bold text-lg text-primary-700 dark:text-primary-400">{coupon.code}</span>
                  {coupon.is_active && <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">Active</span>}
                </div>
                <div className="text-sm text-gray-700 dark:text-gray-200 font-semibold mb-1">{coupon.discount_percentage}% Discount</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{coupon.description}</div>
                {coupon.category && <div className="text-xs text-blue-600 dark:text-blue-400 font-medium">Category: {coupon.category}</div>}
                <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">Valid: {new Date(coupon.valid_from).toLocaleDateString()} - {new Date(coupon.valid_until).toLocaleDateString()}</div>
              </div>
              <div className="flex items-center gap-2 mt-4 sm:mt-0">
                {!coupon.is_active && (
                  <button onClick={() => handleActivate(coupon.id)} className="p-2 rounded bg-green-500 text-white hover:bg-green-600 transition" title="Activate"><Check className="w-4 h-4" /></button>
                )}
                <button onClick={() => handleEdit(coupon)} className="p-2 rounded bg-blue-500 text-white hover:bg-blue-600 transition" title="Edit"><Edit3 className="w-4 h-4" /></button>
                <button onClick={() => handleDelete(coupon.id)} className="p-2 rounded bg-red-500 text-white hover:bg-red-600 transition" title="Delete"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
          {coupons.length === 0 && <div className="text-center text-gray-500 py-12">No coupons found.</div>}
        </div>
      )}
      {/* Modal Form */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-16">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowForm(false)} />
          <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-y-auto animate-fadeInScale">
            <div className="sticky top-0 bg-white dark:bg-gray-900 rounded-t-2xl p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h2 className="text-lg sm:text-xl font-bold">{editingCoupon ? 'Edit Coupon' : 'Add Coupon'}</h2>
                <button onClick={() => setShowForm(false)} className="p-2 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-red-500 hover:text-white transition" title="Close"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <div className="p-4 sm:p-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Coupon Code</label>
                  <input type="text" className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500" value={formData.code} onChange={e => setFormData(f => ({ ...f, code: e.target.value }))} required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <textarea className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500" value={formData.description} onChange={e => setFormData(f => ({ ...f, description: e.target.value }))} rows={2} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Category (Optional)</label>
                  <input type="text" placeholder="e.g., React, Node.js, Full Stack" className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500" value={formData.category} onChange={e => setFormData(f => ({ ...f, category: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Discount Percentage (%)</label>
                  <input type="number" min="1" max="100" className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500" value={formData.discount_percentage} onChange={e => setFormData(f => ({ ...f, discount_percentage: parseInt(e.target.value) }))} required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Valid From</label>
                  <input type="date" className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500" value={formData.valid_from} onChange={e => setFormData(f => ({ ...f, valid_from: e.target.value }))} required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Valid Until</label>
                  <input type="date" className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500" value={formData.valid_until} onChange={e => setFormData(f => ({ ...f, valid_until: e.target.value }))} required />
                </div>
                <div className="flex items-center">
                  <input type="checkbox" id="is_active" className="mr-2" checked={formData.is_active} onChange={e => setFormData(f => ({ ...f, is_active: e.target.checked }))} />
                  <label htmlFor="is_active" className="text-sm font-medium">Active</label>
                </div>
                {error && <div className="text-red-600 bg-red-50 dark:bg-red-900/20 rounded px-3 py-2 text-sm">{error}</div>}
                <div className="sticky bottom-0 bg-white dark:bg-gray-900 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <button type="submit" className="w-full py-2.5 rounded-xl bg-gradient-to-r from-primary-500 to-primary-600 text-white font-bold text-base shadow-lg hover:from-primary-600 hover:to-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200" disabled={saving}>{saving ? <span className="flex items-center justify-center"><RefreshCw className="w-5 h-5 mr-2 animate-spin" />Saving...</span> : (editingCoupon ? 'Update Coupon' : 'Add Coupon')}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      </div>
    </AdminLayout>
  );
};

export default AdminCoupons; 